import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../utils/axiosInstance";
import { socket } from "../socket";
import { useAuth } from "./AuthContext";
import { useWorkspace } from "./WorkspaceContext";

const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { activeWorkspace, loading: workspaceLoading } = useWorkspace() || {};
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadedWorkspaceId = useRef(null);

  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated || workspaceLoading || !activeWorkspace?.id) {
      if (!isAuthenticated) {
        setSubscription(null);
        loadedWorkspaceId.current = null;
      }
      setLoading(false);
      return;
    }
    if (loadedWorkspaceId.current && loadedWorkspaceId.current !== activeWorkspace.id) {
      setSubscription(null);
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/api/me/subscription");
      setSubscription(res.data);
      loadedWorkspaceId.current = activeWorkspace.id;
    } catch (err) {
      console.error("Failed to fetch subscription:", err);
      // Keep the last successful entitlement snapshot during a transient API
      // restart. Clearing it made paid customers see an incorrect upgrade
      // screen whenever the subscription request briefly failed.
      setError(err?.response?.data?.message || "Could not load your plan right now");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, workspaceLoading, activeWorkspace?.id]);

  useEffect(() => {
    fetchSubscription();

    const handleUpdate = () => {
      fetchSubscription();
    };
    socket.on("subscription:update", handleUpdate);

    return () => {
      socket.off("subscription:update", handleUpdate);
    };
  }, [fetchSubscription]);

  return (
    <SubscriptionContext.Provider value={{ subscription, setSubscription, loading, error, refreshSubscription: fetchSubscription }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
